if (!customElements.get('product-info')) {
  const cachedPages = [];

  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        if (this.hasAttribute('data-is-boot')) {
          this.bootVariants = new window.jk.BootVariants(this);
        }

        this.wishlistToggle = this.querySelector('wishlist-toggle');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.initializeDescriptionTabs();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.onPrefetchUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValuePrefetch,
          ({ data: { target } }) => this.contains(target) && this.prefetchProductUrl(target),
        );

        this.initQuantityHandlers();
        if (this.dataset.combinedType === 'options') {
          const combinedMetaText = this.querySelector('[data-combined-meta-variants]')?.innerHTML;
          this.metaVariants = !!combinedMetaText ? JSON.parse(combinedMetaText) : null;
        }
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.onPrefetchUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.bootVariants?.unsubscribe();
      }

      initializeDescriptionTabs() {
        const $description = this.querySelector('.product__description');
        if (!$description || $description.dataset.headingTabs !== 'true') return;
        const openFirst = $description.dataset.openFirst === 'true';
        const markup = $description.querySelector('.accordion__content');
        const title = $description.dataset.heading;
        const tabs = [];
        let currentTab = null;
        let buffer = '';
        markup.childNodes.forEach((node) => {
          const isHeading = node.nodeType === 1 && node.matches?.('h4');
          if (isHeading) {
            const heading = node.textContent.trim();
            if (heading) {
              if (!currentTab && buffer.trim()) {
                tabs.push({
                  heading: title || 'Description',
                  description: buffer,
                });
                buffer = '';
              }
              if (currentTab) {
                tabs.push(currentTab);
              }

              currentTab = {
                heading,
                description: '',
              };
            }
          } else {
            const html = node.outerHTML || node.textContent;
            if (currentTab) {
              currentTab.description += html;
            } else {
              buffer += html;
            }
          }
        });
        if (currentTab) {
          if (currentTab.heading.trim()) {
            tabs.push(currentTab);
          }
        } else if (buffer.trim()) {
          tabs.push({
            heading: title || 'Description',
            description: buffer,
          });
        }

        if (title && tabs.length > 0) {
          tabs[0].heading = title;
        }

        const $details = $description.querySelector('details');
        const tabsName = $description.dataset.multiple !== 'true' ? `name="${$details.name}"` : '';
        const contentId = markup.id;
        const icon = $description.querySelector('.icon.icon-caret')?.outerHTML || '';
        const fragment = document.createDocumentFragment();

        tabs.forEach((tab, idx) => {
          const accordion = document.createElement('div');
          accordion.className = 'accordion';
          accordion.innerHTML = `<details id="${$details.id}-${idx}" ${openFirst && idx === 0 ? 'open' : ''} ${tabsName}><summary role="button" aria-expanded="false" aria-controls="${contentId}-${idx}"><div class="summary__title"><span class="h4 accordion__title inline-richtext uppercase">${tab.heading}</span></div>${icon}</summary><div class="accordion__content rte" id="${contentId}-${idx}">${tab.description}</div></details>`;
          accordion
            .querySelector('summary')
            .addEventListener('click', (event) =>
              event.currentTarget.setAttribute(
                'aria-expanded',
                !event.currentTarget.closest('details').hasAttribute('open'),
              ),
            );
          fragment.appendChild(accordion);
        });
        $description.innerHTML = '';
        $description.appendChild(fragment);
        $description.querySelectorAll('details').forEach((details) => new window.jk.Accordion(details));
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      prefetchProductUrl(target) {
        if (!target.dataset.productUrl || target.dataset.productUrl === this.dataset.url) {
          return;
        }

        const requestUrl = target.dataset.productUrl;
        if (cachedPages.some((p) => p.url === requestUrl)) return;

        const promise = fetch(requestUrl)
          .then((r) => r.text())
          .then((text) => new DOMParser().parseFromString(text, 'text/html'))
          .catch(() => {
            cachedPages.splice(
              cachedPages.findIndex((p) => p.url === requestUrl),
              1,
            );
          });
        cachedPages.push({ url: requestUrl, promise });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.perfStart = performance.now();

        this.resetProductFormState();

        if (target.hasAttribute('data-combined')) {
          if (target.dataset.productUrl === '') {
            const key = Array.from(this.querySelectorAll('input[data-combined]:checked'))
              .map((input) => input.value)
              .join(' ');
            const variant = this.metaVariants[key];
            if (variant && variant.available && variant.url) {
              target.dataset.productUrl = variant.url;
            } else {
              return;
            }
          } else if (target.dataset.productUrl === this.dataset.url) {
            return;
          }
          const mediaGallery = this.querySelector('media-gallery');
          mediaGallery?.classList.add('loading');
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
          swapProduct: shouldSwapProduct,
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          //reinit bold and tabs?
          setTimeout(() => {
            this.reinitBold(productUrl.split('/').pop());
            window.okeWidgetApi && okeWidgetApi.initAllWidgets();
          }, 500);
        };
      }

      reinitBold(productHandle) {
        if (window.BOLD && BOLD.common) {
          BOLD.options.app.productEventNamespaces = [];
          BOLD.options.app.unload();
          const id = { id: Number(this.dataset.productId) };
          Object.assign(BOLD.common.Shopify.products, {
            [productHandle]: id,
          });
          BOLD.common.Shopify.handles[id] = productHandle;
          BOLD.options.app.init();
        }
      }

      renderProductInfo({ requestUrl, targetId, callback, swapProduct }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const finish = (html) => {
          this.pendingRequestUrl = null;
          callback(html);
          document.querySelector(`#${targetId}`)?.focus();
        };

        if (swapProduct) {
          const cached = cachedPages.find((p) => p.url === requestUrl.split('?')[0]);
          if (cached) return cached.promise.then(finish);
        }

        const promise = fetch(requestUrl, { signal: this.abortController.signal })
          .then((r) => r.text())
          .then((text) => new DOMParser().parseFromString(text, 'text/html'));

        if (swapProduct) cachedPages.push({ url: requestUrl.split('?')[0], promise });

        promise.then(finish).catch((error) => {
          if (error.name === 'AbortError') console.log('Fetch aborted by user');
          else console.error(error);
        });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PropAddons');
          updateSourceFromDestination('AvailabilityMessage');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const targetSubmit = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.dataset.atcPrice === 'true' && this.updateSubmitText(targetSubmit);
          this.productForm?.toggleSubmitButton(
            targetSubmit?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut,
          );

          this.bootVariants?.updateInputStatus(this.variantSelectors);
          this.wishlistToggle?.setKey(`${this.dataset.productId}:${variant.id}`);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateSubmitText(targetSubmit) {
        const submitText = targetSubmit?.querySelector('span');
        if (!submitText || !this.productForm) return;
        if (submitText.dataset.addText !== this.productForm.submitButtonText.dataset.addText) {
          this.productForm.submitButtonText.replaceWith(submitText);
          this.productForm.submitButtonText = submitText;
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm?.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
